import React from 'react';
import PropTypes from 'prop-types';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { useDispatch } from 'react-redux';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import Input from '@components/form/Input';
import AddImage from '@/screens/sales-activity/components/todo-list/on-going/image/AddImage';
import Colors from '@global/colors';
import { showImage } from '@/store/global';

const IMAGE_SIZE = 108;
const MAX_DESC = 500;

const CompetitorAttachmentCard = ({
  item,
  index,
  canRemove = true,
  detailData = {},
  detailTodo = {},
  loading = false,
  onUploading = () => {},
  onRemove = () => {},
  onImageUploaded = () => {},
  onDescriptionChange = () => {},
}) => {
  const dispatch = useDispatch();

  return (
    <View testID={`todo-competitor-attachment-${index}`} style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={styles.titleBadge}>
          <SociollaText type="bold" style={styles.titleBadgeText}>
            Attachment #{index + 1}
          </SociollaText>
        </View>
        {canRemove ? (
          <TouchableOpacity
            testID={`todo-competitor-attachment-remove-${index}`}
            activeOpacity={0.7}
            style={styles.removeBtn}
            onPress={onRemove}>
            <Image
              style={styles.removeIcon}
              width={12}
              height={12}
              svg
              source={require('@assets/img/icons/close-grey.svg')}
            />
          </TouchableOpacity>
        ) : (
          <View style={styles.removeBtnPlaceholder} />
        )}
      </View>

      <View style={styles.contentRow}>
        <View style={styles.photoCol}>
          <SociollaText style={styles.fieldLabel}>Photo</SociollaText>
          <View style={styles.imageWrapper}>
            {item?.uri ? (
              <TouchableOpacity
                testID={`todo-competitor-attachment-view-${index}`}
                activeOpacity={0.8}
                onPress={() =>
                  dispatch(
                    showImage({
                      isModal: true,
                      url: { uri: item.uri },
                    }),
                  )
                }>
                <Image
                  style={styles.image}
                  width={IMAGE_SIZE}
                  height={IMAGE_SIZE}
                  source={{ uri: item.uri }}
                />
              </TouchableOpacity>
            ) : (
              <AddImage
                data={[item]}
                setData={(uploaded) => onImageUploaded(uploaded)}
                loadingCallback={(val) => onUploading(val)}
                disabled={loading}
                additional={{
                  activity: detailData,
                  task: detailTodo,
                  showImageOption: true,
                  limit: 1,
                }}
              />
            )}
          </View>
        </View>

        <View style={styles.descCol}>
          <View style={styles.descHeader}>
            <SociollaText style={styles.fieldLabel}>Description</SociollaText>
            <SociollaText style={styles.counterText}>
              {(item?.text || '').length}/{MAX_DESC}
            </SociollaText>
          </View>
          <Input
            label=""
            multiline
            value={item?.text || ''}
            editable={!loading}
            style={styles.descriptionInput}
            containerStyle={styles.descriptionContainer}
            onChangeText={(val) =>
              onDescriptionChange((val || '').slice(0, MAX_DESC))
            }
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 12,
    backgroundColor: Colors.white,
    marginBottom: 12,
    padding: 14,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  titleBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: Colors.grey16,
    borderWidth: 1,
    borderColor: Colors.grey0,
  },
  titleBadgeText: {
    fontSize: 11,
    color: Colors.grey2,
  },
  removeBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    backgroundColor: Colors.grey5,
  },
  removeBtnPlaceholder: {
    width: 28,
    height: 28,
  },
  removeIcon: {
    width: 12,
    height: 12,
  },
  contentRow: {
    flexDirection: 'row',
  },
  photoCol: {
    width: IMAGE_SIZE + 8,
    marginRight: 12,
  },
  descCol: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 11,
    color: Colors.grey2,
    marginBottom: 6,
  },
  imageWrapper: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: Colors.grey16,
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
  },
  descHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  counterText: {
    fontSize: 11,
    color: Colors.grey1,
  },
  descriptionContainer: {
    minHeight: IMAGE_SIZE,
    maxHeight: IMAGE_SIZE,
    borderRadius: 8,
  },
  descriptionInput: {
    minHeight: IMAGE_SIZE - 18,
    textAlignVertical: 'top',
    fontSize: 13,
    color: Colors.grey2,
    paddingTop: 8,
  },
});

CompetitorAttachmentCard.propTypes = {
  item: PropTypes.object,
  index: PropTypes.number,
  canRemove: PropTypes.bool,
  detailData: PropTypes.object,
  detailTodo: PropTypes.object,
  loading: PropTypes.bool,
  onUploading: PropTypes.func,
  onRemove: PropTypes.func,
  onImageUploaded: PropTypes.func,
  onDescriptionChange: PropTypes.func,
};

export default React.memo(CompetitorAttachmentCard);
